import React, { useEffect, useCallback, useState, useMemo } from "react"
import { getYear } from "date-fns"
import PropTypes from "prop-types"
import { useFormik } from "formik"
import { isEmpty } from "lodash"
import { useHistory } from "react-router-dom"
import countries from "i18n-iso-countries"
import countriesFR from "i18n-iso-countries/langs/fr.json"

import "react-datepicker/dist/react-datepicker.css"

// Components
import WithMenu from "../../../../components/templates/WithMenu/WithMenu"
import Editor from "../../../../components/organisms/Editor/Editor"
import Archive from "../../../../components/molecules/Archive/Archive"
import ProductArchiveWizard from "../../../../components/molecules/ProductArchiveWizard/ProductArchiveWizard"
import Spinner from "../../../../components/atoms/Spinner/Spinner"

// Hooks
import useAppContext from "../../../../hooks/useAppContext"
import usePermissionState from "../../../../hooks/usePermissionsState"
import useExtrasApi from "../../../../hooks/Api/useExtrasApi"
import useSeasonsApi from "../../../../hooks/Api/useSeasonsApi"
import useQuotasApi from "../../../../hooks/Api/useQuotasApi"
import useTypesApi from "../../../../hooks/Api/useTypesApi"
import useDurationsApi from "../../../../hooks/Api/useDurationsApi"
import useFormatsApi from "../../../../hooks/Api/useFormatsApi"
import useSportsApi from "../../../../hooks/Api/useSportsApi"
import useMediasApi from "../../../../hooks/Api/useMediasApi"
import useAttachmentsApi from "../../../../hooks/Api/useAttachmentsApi"
import useCategoriesApi from "../../../../hooks/Api/useCategoriesApi"
import useTypologiesApi from "../../../../hooks/Api/useTypologiesApi"
import useTaxRatesApi from "../../../../hooks/Api/useTaxRatesApi"
import usePackagesApi from "../../../../hooks/Api/usePackagesApi"
import useYearsApi from "../../../../hooks/Api/useYearsApi"

// Constants
import { PERMISSIONS, PRODUCTS } from "../../../../constants/constants"

// Utils & misc
import cn from "../../../../utils/cn"

import { exportCollectionToArray, exportCollectionToArrayImages } from "../../../../utils/collection"
import { getInitialLocaleValues } from "../../../../utils/locales"

import styles from "./ExtrasEdit.module.css"
import MediasInput from "../../../../components/atoms/MediasInput/MediasInput"

const VIEWS = {
  FORM: "FORM",
  ARCHIVE: "ARCHIVE",
}

countries.registerLocale(countriesFR)

const permissions = [
  PERMISSIONS.PERM_EXTRAS_UPDATE,
  PERMISSIONS.PERM_EXTRAS_CREATE,
  PERMISSIONS.PERM_EXTRAS_ARCHIVE,
]

const ExtrasEdit = ({ match }) => {
  const familyId = 2
  const {
    context: { locales, userInfo },
  } = useAppContext()
  const rolePermissions = usePermissionState(userInfo, permissions)
  const history = useHistory()

  const [, seasonsData, , { getSeasons }] = useSeasonsApi()
  const [, quotasData, , { getQuotas }] = useQuotasApi()
  const [, typesData, , { getTypes }] = useTypesApi()
  const [, durationsData, , { getDurations }] = useDurationsApi()
  const [, formatsData, , { getFormats }] = useFormatsApi()
  const [, sportsData, , { getSports }] = useSportsApi()
  const [, mediasData, , { getMedias }] = useMediasApi()
  const [, attachmentsData, , { getAttachments }] = useAttachmentsApi()
  const [, categoriesData, , { getCategories }] = useCategoriesApi()
  const [, typologiesData, , { getTypologies }] = useTypologiesApi()
  const [, taxRatesData, , { getTaxRates }] = useTaxRatesApi()
  const [, packagesData, , { getPackages }] = usePackagesApi()
  const [, yearsData, , { getYears }] = useYearsApi()

  const mediasOptions = useMemo(() => (exportCollectionToArrayImages(mediasData, "medias", "fileKey") || []), [mediasData])

  // Extras API Hooks
  const [, extraData, extraError, { getExtraWithId }] = useExtrasApi()
  const [updateExtraState, , updateExtraError, { updateExtra }] = useExtrasApi()
  const [newExtraState, , newExtraError, { createExtra }] = useExtrasApi()

  const isEditingExtra = !isEmpty(match.params)
  const [isUsedInPackage, setIsUsedInPackage] = useState(undefined)
  const [packagesContaining] = useState([])
  const [currentView, setCurrentView] = useState(VIEWS.FORM)

  const savedGetExtraWithId = useCallback(getExtraWithId, [])
  const savedGetSeasons = useCallback(getSeasons, [])
  const savedGetQuotas = useCallback(getQuotas, [])
  const savedGetTypes = useCallback(getTypes, [])
  const savedGetDurations = useCallback(getDurations, [])
  const savedGetFormats = useCallback(getFormats, [])
  const savedGetSports = useCallback(getSports, [])
  const savedGetMedias = useCallback(getMedias, [])
  const savedGetAttachments = useCallback(getAttachments, [])
  const savedGetCategories = useCallback(getCategories, [])
  const savedGetTypologies = useCallback(getTypologies, [])
  const savedTaxRates = useCallback(getTaxRates, [])
  const savedGetPackages = useCallback(getPackages, [])
  const savedGetYears = useCallback(getYears, [])

  const getTaxRateByFamilyId = (first = false) => {
    if (taxRatesData && taxRatesData.taxRates) {
      const taxRatesArray = Object.keys(taxRatesData.taxRates).map(
        taxRateId => taxRatesData.taxRates[taxRateId]
      )

      if (first)
        return taxRatesArray.find(taxRate =>
          taxRate.taxRateFamiliesIds.find(id => id === familyId)
        )
      return taxRatesArray.filter(
        taxRate => !!taxRate.taxRateFamiliesIds.find(id => id === familyId)
      )
    }
    return null
  }

  const getCurrentYearId = () => {
    if (yearsData) {
      const year = getYear(new Date())
      const years = exportCollectionToArray(yearsData, "years")
      return years?.find(y => y.number === year)?.id || years[0].id
    }
    return null
  }
  const currentYearId = getCurrentYearId()

  const createPriceObject = () => {
    return {
      [currentYearId]: Object.keys(seasonsData.seasons).reduce(
        (accumulator, currentValue) => {
          accumulator[currentValue] = 0
          return accumulator
        },
        {}
      ),
    }
  }

  const createInitialValues =
    locales && seasonsData && taxRatesData
      ? {
          product: {
            code: "",
            account: "",
            name: getInitialLocaleValues(locales),
            prices: createPriceObject(),
            taxRateId: getTaxRateByFamilyId(true).id,
            quotasIds: [],
            defaultStock: 0,
            active: true,
          },
          categoryId: 1,
          thumbnailId: 136,
          typologyId: 1,
          subtitle: getInitialLocaleValues(locales),
          description: getInitialLocaleValues(locales),
          showIfWithAccommodations: false,
          requirePickupInfos: false,
          requireDropOffInfos: false,
        }
      : {}

  const {
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
  } = useFormik({
    initialValues: {
      extras: isEditingExtra ? extraData || {} : createInitialValues,
    },
    enableReinitialize: true,
    onSubmit: valuesSubmit => {
      const sanitizedValues = {
        ...valuesSubmit.extras,
        product: { 
          ...valuesSubmit.extras.product,
           archived: false,
          //  prices: Object.keys(valuesSubmit.extras.product.prices).map()
        },
      }
      if (isEditingExtra)
        updateExtra(sanitizedValues).then(() =>
          history.push({
            pathname: "/extras/list",
            state: { submitType: "edit", success: true },
          })
        )
      else
        createExtra(sanitizedValues).then(() =>
          history.push({
            pathname: "/extras/list",
            state: { submitType: "create", success: true },
          })
        )
    },
  })

  const backToList = () => {
    history.push({
      pathname: "/extras/list",
      state: {},
    })
  }

  useEffect(() => {
    if (isEditingExtra) savedGetExtraWithId(match.params.id)
    savedGetSeasons()
    savedGetQuotas({ archived: false })
    savedGetTypes()
    savedGetDurations()
    savedGetFormats()
    savedGetSports()
    savedGetMedias()
    savedGetAttachments()
    savedGetCategories()
    savedGetTypologies()
    savedTaxRates()
    savedGetPackages()
    savedGetYears()
  }, [
    isEditingExtra,
    savedGetExtraWithId,
    savedGetSeasons,
    savedGetQuotas,
    savedGetTypes,
    match.params.id,
    savedGetDurations,
    savedGetFormats,
    savedGetSports,
    savedGetMedias,
    savedGetAttachments,
    savedGetCategories,
    savedGetTypologies,
    savedTaxRates,
    savedGetPackages,
    savedGetYears,
  ])

  const handleCheckboxClick = (field, val) => {
    setFieldValue(field, val)
  }

  const handleExtraArchive = async () => {
    const sanitizedValues = {
      ...values.extras,
      product: { ...values.extras.product, archived: true },
    }
    if (isEditingExtra)
      updateExtra(sanitizedValues).then(() =>
        history.push({
          pathname: "/extras/list",
          state: { submitType: "edit", success: true },
        })
      )
    else
      createExtra(sanitizedValues).then(() =>
        history.push({
          pathname: "/extras/list",
          state: { submitType: "create", success: true },
        })
      )
  }

  const isAllowedToEdit =
    rolePermissions[PERMISSIONS.PERM_EXTRAS_UPDATE] ||
    (rolePermissions[PERMISSIONS.PERM_EXTRAS_CREATE] && !isEditingExtra)

  // When packageData changes, check if product is used in any of them, inactive or not
  useEffect(() => {
    if (packagesData && extraData) {
      setIsUsedInPackage(false)
    }
  }, [packagesData, extraData])

  return (
    <WithMenu>
      <div className="d-flex flex-row py-4 align-items-center justify-content-between">
        <h1>
          {`Extra / ${isEditingExtra ? "Éditer" : "Nouveau"}`}
          {extraData ? ` / ${extraData.product.name["1"]}` : null}
        </h1>
      </div>

      <div className={cn(["mw-100", "w-100", "m-auto", styles.editSection])}>
        {currentView === VIEWS.FORM ? (
          <>
            {values.extras.product &&
            seasonsData &&
            locales &&
            quotasData &&
            durationsData &&
            formatsData &&
            sportsData &&
            mediasData &&
            attachmentsData &&
            categoriesData &&
            typologiesData &&
            taxRatesData &&
            yearsData &&
            typesData ? (
              <form onSubmit={handleSubmit}>
                {extraError || updateExtraError || newExtraError ? (
                  <div className="alert alert-error" role="alert">
                    error
                  </div>
                ) : null}

                <div className={cn(["card mb-4"])}>
                  <div className="card-body">
                    <h5 className="card-title">Informations</h5>

                    <div className="form-row">
                      <div className="col-sm-6 col-md-6 pb-3">
                        <b>
                          <label htmlFor="extra-code">Code</label>
                        </b>
                        <input
                          disabled={!isAllowedToEdit}
                          required
                          type="text"
                          className="form-control"
                          id="extra-code"
                          name="extras.product.code"
                          value={values.extras.product.code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <div className="valid-tooltip">Looks good!</div>
                      </div>
                      <div className="col-sm-6 col-md-6 pb-3">
                        <b>
                          <label htmlFor="extra-account">Account</label>
                        </b>
                        <input
                          disabled={!isAllowedToEdit}
                          required
                          type="text"
                          className="form-control"
                          id="extra-account"
                          name="extras.product.account"
                          value={values.extras.product.account}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <div className="valid-tooltip">Looks good!</div>
                      </div>

                      {Object.keys(locales).map(localeId => (
                        <div
                          key={`name_input_${localeId}`}
                          className="col-sm-4 col-md-4 pb-3"
                        >
                          <b>
                            <label htmlFor={`extra-name-${localeId}`}>{`Name ${locales[
                              localeId
                            ].code.toUpperCase()}`}</label>
                          </b>
                          <input
                            disabled={!isAllowedToEdit}
                            required
                            type="text"
                            className="form-control"
                            id={`extra-name-${localeId}`}
                            name={`extras.product.name.${localeId}`}
                            value={values.extras.product.name[localeId]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <div className="valid-tooltip">Looks good!</div>
                        </div>
                      ))}

                      {!isEditingExtra &&
                        Object.keys(seasonsData.seasons).map(seasonId => (
                          <div
                            key={`price_input_${seasonId}`}
                            className="col-sm-4 col-md-4 pb-3"
                          >
                            <b> 
                              <label htmlFor={`extra-season-${seasonId}`}>{`Saison ${seasonsData.seasons[seasonId].name}`}</label>
                            </b>
                            <div className="input-group">
                              <input
                                disabled={!isAllowedToEdit}
                                required
                                type="number"
                                className="form-control"
                                id={`extra-season-${seasonId}`}
                                name={`extras.product.prices.${currentYearId}.${seasonId}`}
                                value={
                                  values.extras.product.prices[currentYearId][
                                    seasonId
                                  ] / 100
                                }
                                onChange={(e) => setFieldValue(`extras.product.prices.${currentYearId}.${seasonId}`, Number(e.target.value) * 100)}
                                onBlur={handleBlur}
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">€</span>
                              </div>
                            </div>
                            <div className="valid-tooltip">Looks good!</div>
                          </div>
                        ))}

                      <div className="col-sm-6 col-md-6 pb-3">
                        <b>
                          <label htmlFor="extra-defaultStock">Default stock</label>
                        </b>
                        <input
                          disabled={!isAllowedToEdit}
                          required
                          type="number"
                          className="form-control"
                          id="extra-defaultStock"
                          name="extras.product.defaultStock"
                          value={values.extras.product.defaultStock}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <div className="valid-tooltip">Looks good!</div>
                      </div>

                      <div className="col-sm-6 col-md-6 pb-3">
                        <b>
                          <label htmlFor="extra-taxRate">Tax rate</label>
                        </b>
                        <select
                          disabled={!isAllowedToEdit}
                          className="custom-select"
                          id="extra-taxRate"
                          name="extras.product.taxRateId"
                          required
                          onChange={e =>
                            setFieldValue(
                              "extras.product.taxRateId",
                              Number(e.target.value)
                            )
                          }
                          onBlur={handleBlur}
                          value={values.extras.product.taxRateId}
                        >
                          {getTaxRateByFamilyId().map(taxRate => (
                            <option
                              key={`duration_id_select_${taxRate.id}`}
                              value={taxRate.id}
                            >
                              {taxRate.value}
                            </option>
                          ))}
                        </select>
                        <div className="valid-tooltip">Looks good!</div>
                      </div>

                      {/* WIP */}
                      {Object.keys(locales).map(localeId => (
                        <div
                          key={`subtitle_input_${localeId}`}
                          className="col-sm-4 col-md-4 pb-3"
                        >
                          <b>
                            <label htmlFor={`extra-subtitle-${localeId}`}>{`Subtitle ${locales[
                              localeId
                            ].code.toUpperCase()}`}</label>
                          </b>
                          <input
                            disabled={!isAllowedToEdit}
                            required
                            type="text"
                            className="form-control"
                            id={`extra-subtitle-${localeId}`}
                            name={`extras.subtitle.${localeId}`}
                            value={values.extras.subtitle[localeId]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <div className="valid-tooltip">Looks good!</div>
                        </div>
                      ))}

                      {Object.keys(locales).map(localeId => (
                        <div
                          key={`description_input_${localeId}`}
                          className="col-sm-4 col-md-4 pb-3"
                        >
                          <b>
                            <label htmlFor="extra-description">{`Description ${locales[
                              localeId
                            ].code.toUpperCase()}`}</label>
                          </b>
                          <Editor
                            disabled={!isAllowedToEdit}
                            initialContent={values.extras.description[localeId]}
                            handleContentChange={htmlContent =>
                              setFieldValue(
                                `extras.description.${localeId}`,
                                htmlContent
                              )
                            }
                            label={null}
                            error={null}
                          />

                          <div className="valid-tooltip">Looks good!</div>
                        </div>
                      ))}

                      <div className="col-sm-12 col-md-12 pb-3">
                        <b>
                          <label htmlFor="extra-categoryId">
                            Category id
                          </label>
                        </b>
                        <select
                          disabled={!isAllowedToEdit}
                          className="custom-select"
                          id="extra-categoryId"
                          name="extras.categoryId"
                          required
                          onChange={e =>
                            setFieldValue(
                              "extras.categoryId",
                              Number(e.target.value)
                            )
                          }
                          onBlur={handleBlur}
                          value={values.extras.categoryId}
                        >
                          {categoriesData.map(category => (
                            <option
                              key={`category_id_select_${category.id}`}
                              value={category.id}
                            >
                              {category.label}
                            </option>
                          ))}
                        </select>
                        <div className="valid-tooltip">Looks good!</div>
                      </div>

                      <div className="col-sm-12 col-md-12 pb-3">
                        <div className="form-check">
                          <input
                            disabled={!isAllowedToEdit}
                            checked={values.extras.showIfWithAccommodations}
                            className="form-check-input"
                            type="checkbox"
                            onChange={() =>
                              handleCheckboxClick(
                                "extras.showIfWithAccommodations",
                                !values.extras.showIfWithAccommodations
                              )
                            }
                            id="quotas_input_showIfWithAccommodations"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="quotas_input_showIfWithAccommodations"
                          >
                            Ne montrer cet extra que si il est avec une ou
                            plusieurs accommodations
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-12 col-md-12 pb-3">
                        <div className="form-check">
                          <input
                            disabled={!isAllowedToEdit}
                            checked={values.extras.requirePickupInfos}
                            className="form-check-input"
                            type="checkbox"
                            onChange={() =>
                              handleCheckboxClick(
                                "extras.requirePickupInfos",
                                !values.extras.requirePickupInfos
                              )
                            }
                            id="quotas_input_requirePickupInfos"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="quotas_input_requirePickupInfos"
                          >
                            Les infos de pickup sont requises
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-12 col-md-12 pb-3">
                        <div className="form-check">
                          <input
                            disabled={!isAllowedToEdit}
                            checked={values.extras.requireDropOffInfos}
                            className="form-check-input"
                            type="checkbox"
                            onChange={() =>
                              handleCheckboxClick(
                                "extras.requireDropOffInfos",
                                !values.extras.requireDropOffInfos
                              )
                            }
                            id="quotas_input_requireDropOffInfos"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="quotas_input_requireDropOffInfos"
                          >
                            Les infos de dropOff sont requises
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-12 col-md-12 pb-3">
                        <MediasInput
                          label="Thumbnail"
                          options={mediasOptions}
                          selection={values.extras?.thumbnailId ? [values.extras.thumbnailId] : []}
                          setSelection={(value) => setFieldValue('extras.thumbnailId', value[0]?.id || null)}
                          disabled={!isAllowedToEdit}
                          required
                        />
                      </div>

                      <div className="col-sm-12 col-md-12 pb-3">
                        <b>
                          <label htmlFor="extra-typologyId">
                            Typologie
                          </label>
                        </b>
                        <select
                          disabled={!isAllowedToEdit}
                          className="custom-select"
                          id="extra-typologyId"
                          name="extras.typologyId"
                          required
                          onChange={e =>
                            setFieldValue(
                              "extras.typologyId",
                              Number(e.target.value)
                            )
                          }
                          onBlur={handleBlur}
                          value={values.extras.typologyId}
                        >
                          {Object.keys(typologiesData.typologies)
                            .filter(
                              t => typologiesData.typologies[t].familyId === 2
                            )
                            .map(typology => (
                              <option
                                key={`typology_id_select_${typology}`}
                                value={typology}
                              >
                                {typologiesData.typologies[typology].code}
                              </option>
                            ))}
                        </select>
                        <div className="valid-tooltip">Looks good!</div>
                      </div>
                    </div>
                  </div>
                </div>
                {rolePermissions[PERMISSIONS.PERM_EXTRAS_ARCHIVE] &&
                isEditingExtra &&
                isAllowedToEdit ? (
                  <div className="float-right mb-4">
                    {/* {isUsedInPackage ? (
                      <span className="mr-3 text-secondary">
                        Ce produit est utilisé dans un package
                      </span>
                    ) : null} */}
                    <button
                      type="button"
                      onClick={() => setCurrentView(VIEWS.ARCHIVE)}
                      className="btn btn-danger"
                      // disabled={isUsedInPackage}
                    >
                      ARCHIVER
                    </button>
                  </div>
                ) : null}
                <div className="mb-4">
                  {isAllowedToEdit ? (
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={
                        updateExtraState === "loading" ||
                        newExtraState === "loading"
                      }
                    >
                      {updateExtraState === "loading" ||
                      newExtraState === "loading" ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : null}
                      {isEditingExtra ? "SAUVEGARDER" : "CRÉER"}
                    </button>
                  ) : (
                    <button
                      onClick={backToList}
                      type="button"
                      className="btn btn-primary"
                    >
                      Revenir à la liste
                    </button>
                  )}
                </div>
              </form>
            ) : (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            )}
          </>
        ) : null}
        {currentView === VIEWS.ARCHIVE ? (
          <div className={styles.formWidth}>
            <Archive
              onArchivedClick={() => handleExtraArchive()}
              onCancelClick={() => setCurrentView(VIEWS.FORM)}
              customView={isUsedInPackage}
              centered={!isUsedInPackage}
            >
              {isUsedInPackage ? (
                <div>
                  {packagesContaining ? (
                    <ProductArchiveWizard
                      productType={PRODUCTS.EXTRAS}
                      product={extraData}
                      packagesContaining={packagesContaining}
                      handleProductArchive={handleExtraArchive}
                    />
                  ) : (
                    <Spinner />
                  )}
                </div>
              ) : (
                <div className={styles.archiveText}>
                  {`Etes-vous sûr de vouloir archiver l'extra ${extraData.product.name["1"]} ?`}
                </div>
              )}
            </Archive>
          </div>
        ) : null}
      </div>
    </WithMenu>
  )
}

ExtrasEdit.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape(),
  }).isRequired,
}

export default ExtrasEdit
